/**
 * Shared action-button + metric primitives extracted from the Providers page so the
 * State and Modules pages render the same icon-button/tooltip idiom. Both wrap the
 * portal Tooltip, so triggers must be real <button>s (focusable for hover AND keyboard).
 */

import type { ReactNode } from 'react'
import { Tooltip } from '@/shared/components/Tooltip'

/** Tooltip-wrapped square icon button for row-action clusters. `disabledReason` keeps the button
 *  focusable/hoverable (aria-disabled, click suppressed) so the tooltip can still explain WHY -
 *  a hard `disabled` attribute would also suppress the hover/focus events the Tooltip needs.
 *  `tint` colours semantic intent (var(--red)/var(--amber)/var(--green)); `active` swaps the
 *  ghost style for btn-primary (pressed toggles). */
export function IconButton({ label, tip, onClick, disabledReason, busy, tint, active, children }: {
  label: string
  tip: ReactNode
  onClick: () => void
  disabledReason?: string | null
  busy?: boolean
  tint?: string
  active?: boolean
  children: ReactNode
}) {
  const blocked = !!disabledReason || busy
  return (
    <Tooltip text={disabledReason ?? tip} placement="top">
      <button
        type="button"
        className={`btn btn-sm ${active ? 'btn-primary' : 'btn-ghost'}`}
        style={{
          padding: '3px 7px', display: 'inline-flex', alignItems: 'center',
          opacity: disabledReason ? 0.45 : 1,
          ...(tint && !active ? { color: tint, borderColor: tint } : {}),
        }}
        aria-label={label}
        aria-disabled={blocked || undefined}
        aria-pressed={active}
        onClick={(e) => { e.stopPropagation(); if (!blocked) onClick() }}
      >
        {busy ? <span className="spinner" /> : children}
      </button>
    </Tooltip>
  )
}

/** One icon+number metric pair with an explanatory tooltip. The trigger is a tabbable span,
 *  not a button: it must be focusable for the Tooltip (hover AND keyboard focus) but takes no
 *  action - a button here would synthesise Enter-clicks that bubble into row-link navigation.
 *  `tint` overrides the text/icon colour for semantic metrics (e.g. an amber lock indicator). */
export function MetricStat({ icon, value, tip, tint }: { icon: ReactNode; value: ReactNode; tip: ReactNode; tint?: string }) {
  return (
    <Tooltip text={tip} placement="top">
      <span
        tabIndex={0}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'default',
          color: tint ?? 'var(--text2)', fontFamily: 'var(--mono)', fontSize: '0.72rem',
        }}
      >
        <span style={{ color: tint ?? 'var(--text3)', display: 'inline-flex' }}>{icon}</span>
        {value}
      </span>
    </Tooltip>
  )
}
